import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { BookingCard } from "../components/admin/BookingCard";
import { ConfirmDialog } from "../components/admin/ConfirmDialog";
import { CalendarView } from "../components/admin/CalendarView";
import { AdminHeader } from "../components/admin/AdminHeader";
import { ManualAddDialog } from "../components/admin/ManualAddDialog";
import { Loader2, Inbox, List, CalendarDays, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "confirmed", label: "Confirmed" },
  { key: "completed", label: "Completed" },
  { key: "archived", label: "Archived" },
];

export default function AdminDashboard() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("list");
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [showManualAdd, setShowManualAdd] = useState(false);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/appointments`, { headers: authHeaders });
      setItems(data);
    } catch {
      toast.error("Could not load requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/appointments/${id}/status`, { status }, { headers: authHeaders });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this request permanently?")) return;
    try {
      await axios.delete(`${API}/appointments/${id}`, { headers: authHeaders });
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success("Request deleted");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const onConfirmed = (updated) => {
    setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
  };

  const removeReview = async (id) => {
    if (!window.confirm("Remove this review? It will no longer show on your website.")) return;
    try {
      await axios.delete(`${API}/appointments/${id}/review`, { headers: authHeaders });
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, review_rating: null, review_comment: "", review_at: "" } : it)));
      toast.success("Review removed");
    } catch {
      toast.error("Could not remove review.");
    }
  };

  const onCreated = (created) => {
    setItems((prev) => [created, ...prev]);
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);
  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s.key]: items.filter((i) => i.status === s.key).length }), {});

  return (
    <div className="min-h-screen bg-sand">
      <div className="grain-overlay" />
      <div className="relative z-10">
        <AdminHeader active="bookings" />

        <main className="max-w-6xl mx-auto px-6 py-10">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <h1 className="font-heading text-4xl font-light text-charcoal">Bookings</h1>
            <button
              data-testid="add-booking-button"
              onClick={() => setShowManualAdd(true)}
              className="inline-flex items-center gap-2 bg-clay text-sand rounded-full px-5 py-2.5 text-sm font-medium hover:bg-clay-dark transition-colors"
            >
              <Plus size={16} strokeWidth={1.5} /> Add booking
            </button>
          </div>

          {/* View toggle */}
          <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
            <div className="inline-flex bg-sand-dark rounded-full p-1 border border-charcoal/5">
              <button
                data-testid="view-list"
                onClick={() => setView("list")}
                className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm transition-colors ${view === "list" ? "bg-charcoal text-sand" : "text-stone hover:text-charcoal"}`}
              >
                <List size={16} strokeWidth={1.5} /> List
              </button>
              <button
                data-testid="view-calendar"
                onClick={() => setView("calendar")}
                className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm transition-colors ${view === "calendar" ? "bg-charcoal text-sand" : "text-stone hover:text-charcoal"}`}
              >
                <CalendarDays size={16} strokeWidth={1.5} /> Calendar
              </button>
            </div>

            {view === "list" && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  data-testid="filter-all"
                  onClick={() => setFilter("all")}
                  className={`px-4 py-2 rounded-full text-sm transition-colors ${filter === "all" ? "bg-charcoal text-sand" : "bg-sand-dark text-stone hover:text-charcoal"}`}
                >
                  All ({items.length})
                </button>
                {STATUSES.map((s) => (
                  <button
                    key={s.key}
                    data-testid={`filter-${s.key}`}
                    onClick={() => setFilter(s.key)}
                    className={`px-4 py-2 rounded-full text-sm transition-colors ${filter === s.key ? "bg-charcoal text-sand" : "bg-sand-dark text-stone hover:text-charcoal"}`}
                  >
                    {s.label} ({counts[s.key] || 0})
                  </button>
                ))}
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-32 text-stone">
              <Loader2 className="animate-spin" />
            </div>
          ) : view === "calendar" ? (
            <CalendarView items={items} onConfirm={setConfirmTarget} onSetStatus={setStatus} onDelete={remove} onRemoveReview={removeReview} />
          ) : filtered.length === 0 ? (
            <div className="text-center py-32 text-stone" data-testid="admin-empty">
              <Inbox className="mx-auto mb-4 text-clay" size={40} strokeWidth={1.5} />
              <p className="font-heading text-2xl text-charcoal">No requests here yet</p>
              <p className="mt-2">New appointment requests will appear in this list.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="admin-list">
              {filtered.map((a) => (
                <BookingCard key={a.id} a={a} onConfirm={setConfirmTarget} onSetStatus={setStatus} onDelete={remove} onRemoveReview={removeReview} />
              ))}
            </div>
          )}
        </main>
      </div>

      <ConfirmDialog
        target={confirmTarget}
        token={token}
        onClose={() => setConfirmTarget(null)}
        onConfirmed={onConfirmed}
      />

      <ManualAddDialog
        open={showManualAdd}
        token={token}
        onClose={() => setShowManualAdd(false)}
        onCreated={onCreated}
      />
    </div>
  );
}
